import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { GenericModal } from '../../../../../App/global/components/modal';
import { ButtonWrapper } from '../../../../global/components/layout/button-wrapper';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { docuSignStyles } from '../../../../Global/constants/theme/styles/components/docusign-view';
import { TcsmSiteSettingsProps } from '../../../../global/models/epi-model';
import { TcsmStates } from '../../../../root/tcsm-model';
import { GetSignatureUrl } from './docusign-helper';
import { DocuSignConfig, DocuSignRequestOptions, SignerViewResult } from './models/docusign-models';


declare global {
    interface Window { DocuSign: any; }
}

interface Props {
    options: DocuSignRequestOptions;
}

function DocusignView(props: Props) {

    const { options } = props;

    const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state?.epiProps?.siteSettingsState?.siteSettings);

    const { docuSignIss, docuSignJSUrl, docuSignAppBaseUri, docuSignAccountId, docuSignConfigurationErrorMessage, docuSignEnvelopeErrorMessage, docuSignAppFrameAncestorUri } = siteSettings;

    const [showDocusignView, setShowDocusignView] = useState(false);
    const [signerViewResult, setSignerViewResult] = useState<SignerViewResult>(null);
    const [hasEnvelopeCreationError, setHasEnvelopCreationError] = useState<boolean>(false);

    const signatureUrl = signerViewResult?.signatureUrl;
    const siteBaseUri = `${window.location.protocol}//${window.location.host}`;

    const docuSignConfig: DocuSignConfig = {
        docuSignAppBaseUri,
        docuSignAppFrameAncestorUri,
        accountId: docuSignAccountId,
        siteBaseUri,
    };

    const { request, finishText, rejectText } = options;
    const { templateId, subject } = request;

    const hasConfigurationError = (!docuSignIss || !docuSignJSUrl || !docuSignAppBaseUri || !siteBaseUri || !docuSignAccountId) ||
        (!templateId || !subject);

    const docuSignClasses = docuSignStyles();

    function onSigningComplete(eventSignerViewResult: SignerViewResult) {

        options?.onSigningComplete({ envelopeId: eventSignerViewResult?.envelopeId });
        setSignerViewResult(null);
    }

    function onDecline() {

        options?.onDecline();
        setSignerViewResult(null);
    }

    function onSignatureSucess(eventSignerViewResult: SignerViewResult) {
        setSignerViewResult(eventSignerViewResult);
        setShowDocusignView(true);
    }

    function onSignatureFailure() {
        setHasEnvelopCreationError(true);
    }

    useEffect(() => {
        if (!docuSignIss || !window.DocuSign || hasConfigurationError) return;

        if (!signerViewResult) {
            GetSignatureUrl(request, docuSignConfig, onSignatureSucess, onSignatureFailure);
        }

        if (signerViewResult && showDocusignView) {
            window.DocuSign.loadDocuSign(docuSignIss)
                .then((docusign) => {
                    const signing = docusign.signing({
                        url: signatureUrl,
                        displayFormat: 'focused',
                        style: {
                            branding: {
                                primaryButton: {
                                    backgroundColor: '#333',
                                    color: '#fff',
                                },
                            },
                            signingNavigationButton: {
                                finishText: finishText || 'Complete',
                                position: 'bottom-center',
                            },
                        },
                    });

                    signing.on('sessionEnd', (event) => {

                        if (event.sessionEndType === 'signing_complete') {
                            onSigningComplete(signerViewResult);
                            return;
                        }

                        options?.onDecline();
                        setSignerViewResult(null);
                    });

                    signing.mount('#docusign-view');
                })
                .catch((ex) => {
                    // Any configuration or API limits will be caught here
                    console.error('Error loading DocuSign', ex);
                });
        }
    }, [docuSignIss, signatureUrl, showDocusignView]);


    function renderDocusignSignView() {
        if (!showDocusignView) return null;

        return <div className={docuSignClasses.docusignAgreement} id="docusign-view"></div>;
    }

    function renderLoadingView() {
        if (showDocusignView || hasConfigurationError || hasEnvelopeCreationError) return null;

        return <LoadingCircle />;
    }

    function renderConfigError() {
        if (!hasConfigurationError) return null;

        return <ContentSpacer>
            <Banner
                banner={{
                    type: 'error',
                    label: <p><strong>{docuSignConfigurationErrorMessage || 'Missing configuration'}</strong></p>,
                }}
            />
        </ContentSpacer>;
    }

    function renderEnvelopeError() {
        if (!hasEnvelopeCreationError) return null;

        return <ContentSpacer>
            <Banner
                banner={{
                    type: 'error',
                    label: <p><strong>{docuSignEnvelopeErrorMessage || 'Error creating envelop'}</strong></p>,
                }}
            />
        </ContentSpacer>;
    }

    return (
        <GenericModal
            open
            handleClose={onDecline}>
            <div className={docuSignClasses.docusignContanier}>
                {renderConfigError()}
                {renderEnvelopeError()}
                {renderLoadingView()}
                {renderDocusignSignView()}
            </div>
            <ButtonWrapper flexEnd>
                <button className='btn-secondary' type='button' onClick={onDecline} >{rejectText || 'Reject'}</button>
            </ButtonWrapper>
        </GenericModal>
    );
}

export default DocusignView;